import { doc, getDoc, increment, setDoc } from 'firebase/firestore';
import { db } from './firebase';

const MONTHLY_LIMIT = 5;

export async function checkAndIncrementGenerationLimit(userId: string, customerId: string, checkOnly: boolean = false): Promise<boolean> {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const monthStr = `${year}-${(month + 1).toString().padStart(2, '0')}`;
  const docId = `${customerId}-${monthStr}`;

  // Check monthly usage first
  const rateRef = doc(db, 'aiGenerationLimits', docId);
  const rateDoc = await getDoc(rateRef);

  // Get purchased credits
  const userCreditsRef = doc(db, 'userCredits', userId);
  const userCreditsDoc = await getDoc(userCreditsRef);
  const purchasedCredits = userCreditsDoc.exists() ? userCreditsDoc.data().purchasedCredits || 0 : 0;

  // If this is the first generation of the month
  if (!rateDoc.exists()) {
    // If not just checking, set the count to 1
    if (!checkOnly) {
      await setDoc(rateRef, {
        userId,
        month: monthStr,
        count: 1,
        lastUpdated: now,
      });
    }
    return true;
  }

  const data = rateDoc.data();
  const usedMonthly = data.count;

  // If monthly limit is not reached, use monthly credits
  if (usedMonthly < MONTHLY_LIMIT) {
    // Increment the counter if not just checking
    if (!checkOnly) {
      await setDoc(
        rateRef,
        {
          userId,
          month: monthStr,
          count: increment(1),
          lastUpdated: now,
        },
        { merge: true }
      );
    }
    return true;
  }

  // If monthly limit is reached, check for purchased credits
  if (purchasedCredits > 0) {
    // Decrement purchased credits if not just checking
    if (!checkOnly) {
      await setDoc(
        userCreditsRef,
        {
          purchasedCredits: increment(-1),
          lastUpdated: now,
        },
        { merge: true }
      );
    }
    return true;
  }

  // No credits available
  return false;
}

export async function getRemainingGenerations(customerId: string): Promise<number> {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const monthStr = `${year}-${(month + 1).toString().padStart(2, '0')}`;
  const docId = `${customerId}-${monthStr}`;

  const rateRef = doc(db, 'aiGenerationLimits', docId);
  const rateDoc = await getDoc(rateRef);

  const monthlyUsed = rateDoc.exists() ? rateDoc.data().count || 0 : 0;
  const monthlyRemaining = Math.max(0, MONTHLY_LIMIT - monthlyUsed);

  return monthlyRemaining;
}

// Get total available credits (monthly + purchased)
export async function getTotalAvailableCredits(userId: string, customerId: string): Promise<number> {
  // Get monthly remaining credits
  const monthlyRemaining = await getRemainingGenerations(customerId);

  // Get purchased credits
  const userCreditsRef = doc(db, 'userCredits', userId);
  const userCreditsDoc = await getDoc(userCreditsRef);
  const purchasedCredits = userCreditsDoc.exists() ? userCreditsDoc.data().purchasedCredits || 0 : 0;

  return monthlyRemaining + purchasedCredits;
}
